import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, Input, Select, Divider,
  useToast, Badge, SimpleGrid, Image, Link, Tag, TagLabel, TagCloseButton,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiTrash2, FiCheckCircle, FiPlus } from 'react-icons/fi';
import { recorridosApi, UPLOADS_BASE, Recorrido } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  recorridoId: number | null;
  onCambios?: () => void;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={2} mt={2}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const ModalRecorridoDetalle: React.FC<Props> = ({ isOpen, onClose, recorridoId, onCambios }) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rec, setRec] = useState<Recorrido | null>(null);
  const [cargando, setCargando] = useState(false);

  // calle
  const [calle, setCalle] = useState('');
  const [calleColonia, setCalleColonia] = useState('');
  const [calleObs, setCalleObs] = useState('');
  // personal
  const [pNombre, setPNombre] = useState('');
  const [pRol, setPRol] = useState('');
  // incidencia
  const [iTipo, setITipo] = useState('');
  const [iDesc, setIDesc] = useState('');
  const [iGravedad, setIGravedad] = useState('');
  // archivo
  const [archivo, setArchivo] = useState<File | null>(null);
  const [comentario, setComentario] = useState('');

  const cargar = async () => {
    if (!recorridoId) return;
    setCargando(true);
    try { setRec(await recorridosApi.get(recorridoId)); } catch { setRec(null); } finally { setCargando(false); }
  };

  useEffect(() => { if (isOpen && recorridoId) cargar(); /* eslint-disable-next-line */ }, [isOpen, recorridoId]);

  const refresh = async () => { await cargar(); onCambios?.(); };
  const err = (e: any) => toast({ title: 'Error', description: e?.response?.data?.message || 'Ocurrió un error.', status: 'error', duration: 4000 });

  const addCalle = async () => {
    if (!recorridoId || !calle) { toast({ title: 'Ingresa el nombre de la calle', status: 'warning', duration: 2500 }); return; }
    try { await recorridosApi.addCalle(recorridoId, { calle, colonia: calleColonia, observaciones: calleObs }); setCalle(''); setCalleColonia(''); setCalleObs(''); refresh(); } catch (e) { err(e); }
  };
  const atenderCalle = async (cid: number) => {
    if (!recorridoId) return;
    try { await recorridosApi.atenderCalle(recorridoId, cid, { atendida: true, horaAtencion: new Date().toTimeString().slice(0, 5) }); refresh(); } catch (e) { err(e); }
  };
  const removeCalle = async (cid: number) => { if (!recorridoId) return; try { await recorridosApi.removeCalle(recorridoId, cid); refresh(); } catch (e) { err(e); } };

  const addPersonal = async () => {
    if (!recorridoId || !pNombre) { toast({ title: 'Ingresa el nombre', status: 'warning', duration: 2500 }); return; }
    try { await recorridosApi.addPersonal(recorridoId, { nombre: pNombre, rol: pRol }); setPNombre(''); setPRol(''); refresh(); } catch (e) { err(e); }
  };
  const removePersonal = async (pid: number) => { if (!recorridoId) return; try { await recorridosApi.removePersonal(recorridoId, pid); refresh(); } catch (e) { err(e); } };

  const addIncidencia = async () => {
    if (!recorridoId || !iDesc) { toast({ title: 'Ingresa la descripción', status: 'warning', duration: 2500 }); return; }
    try { await recorridosApi.addIncidencia(recorridoId, { tipo: iTipo, descripcion: iDesc, gravedad: iGravedad }); setITipo(''); setIDesc(''); setIGravedad(''); refresh(); } catch (e) { err(e); }
  };
  const removeIncidencia = async (iid: number) => { if (!recorridoId) return; try { await recorridosApi.removeIncidencia(recorridoId, iid); refresh(); } catch (e) { err(e); } };

  const subirArchivo = async () => {
    if (!recorridoId || !archivo) { toast({ title: 'Selecciona un archivo', status: 'warning', duration: 2500 }); return; }
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('tipoArchivo', archivo.type.startsWith('image/') ? 'foto' : 'documento');
      if (comentario) fd.append('comentario', comentario);
      await recorridosApi.addArchivo(recorridoId, fd);
      setArchivo(null); setComentario('');
      toast({ title: 'Evidencia subida', status: 'success', duration: 2500 });
      refresh();
    } catch (e) { err(e); }
  };
  const removeArchivo = async (aid: number) => { if (!recorridoId) return; try { await recorridosApi.removeArchivo(recorridoId, aid); refresh(); } catch (e) { err(e); } };

  const calles = rec?.calles || [];
  const personal = rec?.personal || [];
  const incidencias = rec?.incidencias || [];
  const archivos = rec?.archivos || [];
  const isImg = (a: any) => /\.(png|jpe?g|gif|webp)$/i.test(a.rutaArchivo || '');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
          Detalle del Recorrido — {rec?.folio || ''}
        </ModalHeader>
        <ModalBody py={5}>
          {cargando ? (
            <Text color="gray.500" textAlign="center" py={6}>Cargando...</Text>
          ) : (
            <VStack spacing={5} align="stretch">
              {/* CALLES */}
              <Box>
                <SectionTitle>Calles atendidas</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2} mb={2}>
                  <Input size="sm" placeholder="Calle *" value={calle} onChange={(e) => setCalle(e.target.value)} />
                  <Input size="sm" placeholder="Colonia" value={calleColonia} onChange={(e) => setCalleColonia(e.target.value)} />
                  <Input size="sm" placeholder="Observaciones" value={calleObs} onChange={(e) => setCalleObs(e.target.value)} />
                  <Button size="sm" leftIcon={<FiPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={addCalle}>Agregar</Button>
                </SimpleGrid>
                {calles.length === 0 ? <Text fontSize="sm" color="gray.400">Sin calles registradas.</Text> : (
                  <VStack align="stretch" spacing={1}>
                    {calles.map((c) => (
                      <HStack key={c.id} justify="space-between" bg="gray.50" p={2} borderRadius="md">
                        <HStack>
                          <Badge colorScheme={c.atendida ? 'green' : 'gray'}>{c.atendida ? 'Atendida' : 'Pendiente'}</Badge>
                          <Text fontSize="sm">{c.calle}{c.colonia ? ` — ${c.colonia}` : ''}{c.horaAtencion ? ` (${c.horaAtencion})` : ''}</Text>
                        </HStack>
                        <HStack>
                          {!c.atendida && <Button size="xs" colorScheme="green" variant="outline" leftIcon={<FiCheckCircle />} onClick={() => atenderCalle(c.id)}>Marcar atendida</Button>}
                          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeCalle(c.id)}><Icon as={FiTrash2} /></Button>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>

              {/* PERSONAL */}
              <Box>
                <SectionTitle>Personal</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} mb={2}>
                  <Input size="sm" placeholder="Nombre *" value={pNombre} onChange={(e) => setPNombre(e.target.value)} />
                  <Input size="sm" placeholder="Rol" value={pRol} onChange={(e) => setPRol(e.target.value)} />
                  <Button size="sm" leftIcon={<FiPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={addPersonal}>Agregar</Button>
                </SimpleGrid>
                {personal.length === 0 ? <Text fontSize="sm" color="gray.400">Sin personal registrado.</Text> : (
                  <HStack flexWrap="wrap" spacing={2}>
                    {personal.map((p) => (
                      <Tag key={p.id} colorScheme="red" borderRadius="full" size="md">
                        <TagLabel>{p.nombre}{p.rol ? ` (${p.rol})` : ''}</TagLabel>
                        <TagCloseButton onClick={() => removePersonal(p.id)} />
                      </Tag>
                    ))}
                  </HStack>
                )}
              </Box>

              {/* INCIDENCIAS */}
              <Box>
                <SectionTitle>Incidencias</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2} mb={2}>
                  <Input size="sm" placeholder="Tipo" value={iTipo} onChange={(e) => setITipo(e.target.value)} />
                  <Input size="sm" placeholder="Descripción *" value={iDesc} onChange={(e) => setIDesc(e.target.value)} />
                  <Select size="sm" placeholder="Gravedad" value={iGravedad} onChange={(e) => setIGravedad(e.target.value)}>
                    <option value="Baja">Baja</option><option value="Media">Media</option><option value="Alta">Alta</option>
                  </Select>
                  <Button size="sm" leftIcon={<FiPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={addIncidencia}>Agregar</Button>
                </SimpleGrid>
                {incidencias.length === 0 ? <Text fontSize="sm" color="gray.400">Sin incidencias.</Text> : (
                  <VStack align="stretch" spacing={1}>
                    {incidencias.map((i) => (
                      <HStack key={i.id} justify="space-between" bg="gray.50" p={2} borderRadius="md">
                        <HStack>
                          {i.gravedad && <Badge colorScheme={i.gravedad === 'Alta' ? 'red' : i.gravedad === 'Media' ? 'orange' : 'yellow'}>{i.gravedad}</Badge>}
                          <Text fontSize="sm">{i.tipo ? `[${i.tipo}] ` : ''}{i.descripcion}</Text>
                        </HStack>
                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeIncidencia(i.id)}><Icon as={FiTrash2} /></Button>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>

              <Divider />

              {/* EVIDENCIAS */}
              <Box>
                <SectionTitle>Evidencias / Archivos</SectionTitle>
                <HStack mb={3} flexWrap="wrap">
                  <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
                  <Button size="sm" leftIcon={<FiUpload />} variant="outline" colorScheme="teal" onClick={() => fileRef.current?.click()}>
                    {archivo ? archivo.name : 'Seleccionar archivo'}
                  </Button>
                  <Input size="sm" placeholder="Comentario" value={comentario} onChange={(e) => setComentario(e.target.value)} w={{ base: 'full', md: '240px' }} />
                  <Button size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={subirArchivo} isDisabled={!archivo}>Subir</Button>
                </HStack>
                {archivos.length === 0 ? <Text fontSize="sm" color="gray.400">Sin evidencias.</Text> : (
                  <HStack spacing={3} flexWrap="wrap" align="start">
                    {archivos.map((a) => (
                      <Box key={a.id} position="relative" w="90px">
                        {isImg(a) ? (
                          <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank">
                            <Image src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} boxSize="90px" objectFit="cover" borderRadius="md" border="1px solid" borderColor="gray.200" />
                          </Box>
                        ) : (
                          <VStack w="90px" h="90px" bg="gray.100" borderRadius="md" justify="center" as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank">
                            <Icon as={FiFile} boxSize={6} color="red.500" />
                            <Text fontSize="2xs" noOfLines={1} px={1}>{a.nombreArchivo}</Text>
                          </VStack>
                        )}
                        <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red" borderRadius="full" p={0} minW="18px" h="18px" onClick={() => removeArchivo(a.id)}>
                          <Icon as={FiTrash2} boxSize={2.5} />
                        </Button>
                      </Box>
                    ))}
                  </HStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalRecorridoDetalle;
